import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  CardHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import * as moment from "moment";
import { Link, useLocation, useNavigate } from "react-router-dom";
import classnames from "classnames";
import Flatpickr from "react-flatpickr";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import DeleteModal from "../../../Components/Common/DeleteModal";
import { isEmpty } from "lodash";
import * as Yup from "yup";
import { useFormik } from "formik";
import Loader from "../../../Components/Common/Loader";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ExportCSVModal from "../../../Components/Common/ExportCSVModal";
import api, { handleApiError } from "../../../helpers/api";
import { useTranslation } from 'react-i18next';

const EcommerceOrders = () => {
  const { t } = useTranslation();
  const [modal, setModal] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [orderList, setOrderList] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [order, setOrder] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);
  const [isExportCSV, setIsExportCSV] = useState(false);
  const [error, setError] = useState(null);
  const [tva, setTva] = useState(null);
  const [tvaInput, setTvaInput] = useState("");
  const [tvaError, setTvaError] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const onClickDelete = (order) => {
    setOrder(order);
    setDeleteModal(true);
  };

  const handleDeleteOrder = async () => {
    if (order) {
      try {
        await api.delete(`/orders/${order._id}`);
        setAllOrders(allOrders.filter((o) => o._id !== order._id));
        setOrderList(orderList.filter((o) => o._id !== order._id));
        setDeleteModal(false);
        toast.success(t("ecommerce_orders.toasts.delete_success"));
      } catch (error) {
        handleApiError(error, setError, navigate);
        toast.error(error.response?.data?.message || t("ecommerce_orders.toasts.delete_error"));
      }
    }
  };

  const toggleTab = (tab, type) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      let filteredOrders = [...allOrders];
      if (type !== "all") {
        filteredOrders = allOrders.filter((order) => order.status === type);
      }
      setOrderList(filteredOrders);
    }
  };

  const queryParams = new URLSearchParams(location.search);
  const clientId = queryParams.get("client");

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const url = clientId ? `/orders?client=${clientId}` : "/orders";
        const response = await api.get(url);
        const normalizedOrders = response.data.data.map((order) => ({
          ...order,
          items: order.items.map((item) => ({
            ...item,
            product: item.product?.product && typeof item.product.product === "object"
                ? item.product.product
                : item.product || { _id: "", name: "Unknown", brand: "N/A" },
          })),
        }));
        setAllOrders(normalizedOrders);
        setOrderList(normalizedOrders);
        setError(null);
      } catch (error) {
        handleApiError(error, setError, navigate);
      }
    };

    const fetchTva = async () => {
      try {
        const response = await api.get("/products/tva");
        setTva(response.data.tva);
        setTvaError(null);
      } catch (error) {
        handleApiError(error, setTvaError, navigate);
        toast.error(error.response?.data?.message || t("ecommerce_orders.toasts.tva_fetch_error"));
      }
    };

    fetchOrders();
    fetchTva();
  }, [clientId, navigate, t]);

  const handleTvaSubmit = async () => {
    try {
      const newTva = parseFloat(tvaInput);
      if (isNaN(newTva) || newTva < 0) {
        toast.error(t("ecommerce_orders.form.tva_invalid"));
        return;
      }
      await api.post("/products/tva", { tva: newTva });
      setTva(newTva);
      setTvaInput("");
      toast.success(t("ecommerce_orders.toasts.tva_update_success"));
    } catch (error) {
      handleApiError(error, setTvaError, navigate);
      toast.error(error.response?.data?.message || t("ecommerce_orders.toasts.tva_update_error"));
    }
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      status: (order && order.status) || "",
      totalPrice: (order && order.totalPrice) || "",
      items: (order && order.items) || [],
    },
    validationSchema: Yup.object({
      status: Yup.string()
          .oneOf(["pending", "confirmed", "shipped", "delivered", "cancelled"])
          .required(t("ecommerce_orders.form.status_required")),
      totalPrice: Yup.number()
          .min(0, t("ecommerce_orders.form.total_price_non_negative"))
          .required(t("ecommerce_orders.form.total_price_required")),
      items: Yup.array()
          .of(
              Yup.object({
                product: Yup.object()
                    .shape({
                      _id: Yup.string().required(t("ecommerce_orders.form.product_id_required")),
                      name: Yup.string().required(t("ecommerce_orders.form.product_name_required")),
                      brand: Yup.string().optional(),
                      image: Yup.string().optional(),
                    })
                    .required(t("ecommerce_orders.form.product_required")),
                quantity: Yup.number()
                    .min(1, t("ecommerce_orders.form.quantity_min"))
                    .required(t("ecommerce_orders.form.quantity_required")),
                price: Yup.number()
                    .min(0, t("ecommerce_orders.form.price_non_negative"))
                    .required(t("ecommerce_orders.form.price_required")),
              })
          )
          .required(t("ecommerce_orders.form.items_required")),
    }),
    onSubmit: async (values) => {
      try {
        const updateOrder = {
          status: values.status,
          totalPrice: values.totalPrice,
          items: values.items.map((item) => ({
            product: item.product?._id || "",
            quantity: item.quantity,
            price: item.price,
            customSpecs: item.customSpecs,
          })),
        };
        const response = await api.patch(`/orders/${order._id}`, updateOrder);
        const updatedOrder = response.data.data;
        const normalizedOrder = {
          ...updatedOrder,
          items: updatedOrder.items.map((item) => ({
            ...item,
            product: item.product?.product && typeof item.product.product === "object"
                ? item.product.product
                : item.product || { _id: "", name: "Unknown", brand: "N/A" },
          })),
        };
        setAllOrders(allOrders.map((o) => (o._id === order._id ? normalizedOrder : o)));
        setOrderList(orderList.map((o) => (o._id === order._id ? normalizedOrder : o)));
        validation.resetForm();
        toggle();
        toast.success(t("ecommerce_orders.toasts.update_success"));
      } catch (error) {
        handleApiError(error, setError, navigate);
        toast.error(error.response?.data?.message || t("ecommerce_orders.toasts.update_error"));
      }
    },
  });

  const toggle = useCallback(() => {
    setModal(!modal);
    if (modal) setOrder(null);
  }, [modal]);

  const handleOrderClick = useCallback(
      (arg) => {
        const order = arg;
        setOrder({
          _id: order._id,
          status: order.status,
          totalPrice: order.totalPrice,
          items: order.items.map((item) => ({
            product: item.product?.product && typeof item.product.product === "object"
                ? item.product.product
                : item.product || { _id: "", name: "Unknown", brand: "N/A" },
            quantity: item.quantity,
            price: item.price,
            customSpecs: item.customSpecs,
          })),
        });
        setIsEdit(true);
        toggle();
      },
      [toggle]
  );

  const handleRemoveProduct = (index) => {
    const updatedItems = [...validation.values.items];
    updatedItems.splice(index, 1);
    validation.setFieldValue("items", updatedItems);
    const newTotalPrice = updatedItems.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0
    );
    validation.setFieldValue("totalPrice", newTotalPrice);
  };

  const checkedAll = useCallback(() => {
    const checkall = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".orderCheckBox");
    ele.forEach((el) => (el.checked = checkall.checked));
    deleteCheckbox();
  }, []);

  const [selectedCheckBoxDelete, setSelectedCheckBoxDelete] = useState([]);
  const [isMultiDeleteButton, setIsMultiDeleteButton] = useState(false);

  const deleteMultiple = async () => {
    try {
      for (const element of selectedCheckBoxDelete) {
        await api.delete(`/orders/${element.value}`);
      }
      setAllOrders(
          allOrders.filter((o) => !selectedCheckBoxDelete.some((el) => el.value === o._id))
      );
      setOrderList(
          orderList.filter((o) => !selectedCheckBoxDelete.some((el) => el.value === o._id))
      );
      setIsMultiDeleteButton(false);
      document.getElementById("checkBoxAll").checked = false;
      toast.success(t("ecommerce_orders.toasts.multi_delete_success"));
    } catch (error) {
      handleApiError(error, setError, navigate);
      toast.error(error.response?.data?.message || t("ecommerce_orders.toasts.multi_delete_error"));
    }
  };

  const deleteCheckbox = () => {
    const ele = document.querySelectorAll(".orderCheckBox:checked");
    setIsMultiDeleteButton(ele.length > 0);
    setSelectedCheckBoxDelete(ele);
  };

  const columns = useMemo(
      () => [
        {
          Header: (
              <input
                  type="checkbox"
                  id="checkBoxAll"
                  className="form-check-input"
                  onClick={checkedAll}
              />
          ),
          Cell: (cellProps) => (
              <input
                  type="checkbox"
                  className="orderCheckBox form-check-input"
                  value={cellProps.row.original._id}
                  onChange={deleteCheckbox}
              />
          ),
          id: "#",
        },
        {
          Header: t("ecommerce_orders.table.order_reference"),
          accessor: "reference",
          filterable: false,
          Cell: (cell) => (
              <Link
                  to={`/apps-ecommerce-order-details/${cell.row.original._id}`}
                  className="fw-medium link-primary"
              >
                {cell.value}
              </Link>
          ),
        },
        {
          Header: t("ecommerce_orders.table.customer"),
          accessor: "user",
          filterable: false,
          Cell: (cell) => `${cell.value?.firstname || "N/A"} ${cell.value?.lastname || "N/A"}`,
        },
        {
          Header: t("ecommerce_orders.table.order_date"),
          accessor: "createdAt",
          filterable: true,
          Cell: (order) => (
              <>
                {handleValidDate(order.row.original.createdAt)},
                <small className="text-muted">
                  {" "}
                  {handleValidTime(order.row.original.createdAt)}
                </small>
              </>
          ),
        },
        {
          Header: t("ecommerce_orders.table.amount"),
          accessor: "totalPrice",
          filterable: false,
        },
        {
          Header: t("ecommerce_orders.table.delivery_status"),
          accessor: "status",
          Cell: (cell) => {
            const status = cell.value;
            const statusClasses = {
              pending: "badge-soft-warning",
              confirmed: "badge-soft-info",
              shipped: "badge-soft-primary",
              delivered: "badge-soft-success",
              cancelled: "badge-soft-danger",
            };
            return (
                <span className={`badge text-uppercase ${statusClasses[status] || "badge-soft-warning"}`}>
              {t(`ecommerce_orders.status.${status}`)}
            </span>
            );
          },
        },
        {
          Header: t("ecommerce_orders.table.action"),
          Cell: (cellProps) => (
              <ul className="list-inline hstack gap-2 mb-0">
                <li className="list-inline-item">
                  <Link
                      to={`/apps-ecommerce-order-details/${cellProps.row.original._id}`}
                      className="text-primary d-inline-block"
                  >
                    <i className="ri-eye-fill fs-16"></i>
                  </Link>
                </li>
                <li className="list-inline-item edit">
                  <Link
                      to="#"
                      className="text-primary d-inline-block edit-item-btn"
                      onClick={() => handleOrderClick(cellProps.row.original)}
                  >
                    <i className="ri-pencil-fill fs-16"></i>
                  </Link>
                </li>
                <li className="list-inline-item">
                  <Link
                      to="#"
                      className="text-danger d-inline-block remove-item-btn"
                      onClick={() => onClickDelete(cellProps.row.original)}
                  >
                    <i className="ri-delete-bin-5-fill fs-16"></i>
                  </Link>
                </li>
              </ul>
          ),
        },
      ],
      [handleOrderClick, checkedAll, t]
  );

  const handleValidDate = (date) => moment(new Date(date)).format("DD MMM Y");

  const handleValidTime = (time) => {
    const time1 = new Date(time);
    const getHour = time1.getUTCHours();
    const getMin = time1.getUTCMinutes();
    const getTime = `${getHour}:${getMin}`;
    const meridiem = getHour >= 12 ? "PM" : "AM";
    return `${moment(getTime, "hh:mm").format("hh:mm")} ${meridiem}`;
  };

  const csvData = useMemo(() => {
    return orderList.map((order) => ({
      orderReference: order.reference,
      customer: `${order.user?.firstname || "N/A"} ${order.user?.lastname || "N/A"}`,
      orderDate: `${handleValidDate(order.createdAt)} ${handleValidTime(order.createdAt)}`,
      amount: order.totalPrice,
      status: t(`ecommerce_orders.status.${order.status}`),
      products: order.items
          .map((item) => `${item.product?.name || t("ecommerce_orders.unknown_product")} (Brand: ${item.product?.brand || "N/A"}, Price: ${item.price}, Quantity: ${item.quantity})`)
          .join("; "),
    }));
  }, [orderList, t]);

  const [date, setDate] = useState(() => {
    let d = new Date(),
        months = [
          t("ecommerce_orders.months.jan"),
          t("ecommerce_orders.months.feb"),
          t("ecommerce_orders.months.mar"),
          t("ecommerce_orders.months.apr"),
          t("ecommerce_orders.months.may"),
          t("ecommerce_orders.months.jun"),
          t("ecommerce_orders.months.jul"),
          t("ecommerce_orders.months.aug"),
          t("ecommerce_orders.months.sep"),
          t("ecommerce_orders.months.oct"),
          t("ecommerce_orders.months.nov"),
          t("ecommerce_orders.months.dec"),
        ];
    let h = d.getHours() % 12 || 12;
    let ampm = d.getHours() < 12 ? t("ecommerce_orders.time.am") : t("ecommerce_orders.time.pm");
    return `${d.getDate()} ${months[d.getMonth()]}, ${d.getFullYear()}, ${h}:${d.getMinutes()} ${ampm}`;
  });

  document.title = t("ecommerce_orders.title");

  return (
      <div className="page-content">
        <ExportCSVModal
            show={isExportCSV}
            onCloseClick={() => setIsExportCSV(false)}
            data={csvData}
        />
        <DeleteModal
            show={deleteModal}
            onDeleteClick={handleDeleteOrder}
            onCloseClick={() => setDeleteModal(false)}
        />
        <DeleteModal
            show={deleteModalMulti}
            onDeleteClick={() => {
              deleteMultiple();
              setDeleteModalMulti(false);
            }}
            onCloseClick={() => setDeleteModalMulti(false)}
        />
        <Container fluid>
          <BreadCrumb title={t("ecommerce_orders.title")} pageTitle={t("ecommerce_orders.page_title")} />
          <Row>
            <Col lg={12}>
              <Card id="orderList">
                <CardHeader className="card-header border-0">
                  <Row className="align-items-center gy-3">
                    <div className="col-sm">
                      <h5 className="card-title mb-0">{t("ecommerce_orders.order_history")}</h5>
                    </div>
                    <div className="col-sm-auto">
                      <div className="d-flex gap-1 flex-wrap align-items-center">
                        <button
                            type="button"
                            className="btn btn-info"
                            onClick={() => setIsExportCSV(true)}
                        >
                          <i className="ri-file-download-line align-bottom me-1"></i>{" "}
                          {t("ecommerce_orders.actions.export")}
                        </button>
                        <div className="d-flex gap-2 align-items-center">
                          <span>{t("ecommerce_orders.tva.label")}: {tva !== null ? `${tva}%` : t("ecommerce_orders.tva.loading")}</span>
                          <Input
                              type="number"
                              placeholder={t("ecommerce_orders.tva.placeholder")}
                              value={tvaInput}
                              onChange={(e) => setTvaInput(e.target.value)}
                              style={{ width: "100px" }}
                          />
                          <button
                              type="button"
                              className="btn btn-success btn-sm"
                              onClick={handleTvaSubmit}
                              disabled={!tvaInput}
                          >
                            {t("ecommerce_orders.tva.update")}
                          </button>
                        </div>
                        {isMultiDeleteButton && (
                            <button
                                className="btn btn-soft-danger"
                                onClick={() => setDeleteModalMulti(true)}
                            >
                              <i className="ri-delete-bin-2-line"></i>
                            </button>
                        )}
                      </div>
                    </div>
                  </Row>
                </CardHeader>
                <CardBody className="pt-0">
                  <div>
                    <Nav
                        className="nav-tabs nav-tabs-custom nav-success"
                        role="tablist"
                    >
                      <NavItem>
                        <NavLink
                            className={classnames({ active: activeTab === "1" })}
                            onClick={() => toggleTab("1", "all")}
                            href="#"
                        >
                          <i className="ri-store-2-fill me-1 align-bottom"></i> {t("ecommerce_orders.tabs.all_orders")}
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                            className={classnames({ active: activeTab === "2" })}
                            onClick={() => toggleTab("2", "delivered")}
                            href="#"
                        >
                          <i className="ri-checkbox-circle-line me-1 align-bottom"></i> {t("ecommerce_orders.tabs.delivered")}
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                            className={classnames({ active: activeTab === "3" })}
                            onClick={() => toggleTab("3", "shipped")}
                            href="#"
                        >
                          <i className="ri-truck-line me-1 align-bottom"></i> {t("ecommerce_orders.tabs.shipped")}
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                            className={classnames({ active: activeTab === "4" })}
                            onClick={() => toggleTab("4", "confirmed")}
                            href="#"
                        >
                          <i className="ri-arrow-left-right-fill me-1 align-bottom"></i> {t("ecommerce_orders.tabs.confirmed")}
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                            className={classnames({ active: activeTab === "5" })}
                            onClick={() => toggleTab("5", "cancelled")}
                            href="#"
                        >
                          <i className="ri-close-circle-line me-1 align-bottom"></i> {t("ecommerce_orders.tabs.cancelled")}
                        </NavLink>
                      </NavItem>
                    </Nav>
                    {!error && orderList?.length ? (
                        <TableContainer
                            columns={columns}
                            data={orderList || []}
                            isGlobalFilter={true}
                            isAddUserList={false}
                            customPageSize={8}
                            divClass="table-responsive table-card mb-1"
                            tableClass="align-middle table-nowrap"
                            theadClass="table-light text-muted text-uppercase"
                            isFilterDate={true}
                            SearchPlaceholder={t("ecommerce_orders.table.search_placeholder")}
                        />
                    ) : !error ? (
                        <div className="text-center py-4">
                          <h5>{t("ecommerce_orders.table.no_orders_found")}</h5>
                        </div>
                    ) : (
                        <Loader error={error} />
                    )}
                  </div>
                  <Modal id="showModal" isOpen={modal} toggle={toggle} centered>
                    <ModalHeader className="bg-light p-3" toggle={toggle}>
                      {t("ecommerce_orders.modal.edit_order")}
                    </ModalHeader>
                    <form
                        className="tablelist-form"
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                    >
                      <ModalBody>
                        <div className="mb-3">
                          <Label htmlFor="status-field" className="form-label">
                            {t("ecommerce_orders.form.status")}
                          </Label>
                          <Input
                              name="status"
                              type="select"
                              className="form-select"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.status || ""}
                              invalid={validation.touched.status && validation.errors.status}
                          >
                            <option value="">{t("ecommerce_orders.form.status_placeholder")}</option>
                            <option value="pending">{t("ecommerce_orders.status.pending")}</option>
                            <option value="confirmed">{t("ecommerce_orders.status.confirmed")}</option>
                            <option value="shipped">{t("ecommerce_orders.status.shipped")}</option>
                            <option value="delivered">{t("ecommerce_orders.status.delivered")}</option>
                            <option value="cancelled">{t("ecommerce_orders.status.cancelled")}</option>
                          </Input>
                          {validation.touched.status && validation.errors.status && (
                              <FormFeedback type="invalid">{validation.errors.status}</FormFeedback>
                          )}
                        </div>
                        <div className="mb-3">
                          <Label htmlFor="totalPrice-field" className="form-label">
                            {t("ecommerce_orders.form.total_price")}
                          </Label>
                          <Input
                              name="totalPrice"
                              type="number"
                              className="form-control"
                              placeholder={t("ecommerce_orders.form.total_price_placeholder")}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.totalPrice || ""}
                              invalid={validation.touched.totalPrice && validation.errors.totalPrice}
                          />
                          {validation.touched.totalPrice && validation.errors.totalPrice && (
                              <FormFeedback type="invalid">{validation.errors.totalPrice}</FormFeedback>
                          )}
                        </div>
                        <div className="mb-3">
                          <Label className="form-label">{t("ecommerce_orders.form.products")}</Label>
                          {validation.values.items?.length > 0 ? (
                              validation.values.items.map((item, index) => (
                                  <div
                                      key={index}
                                      className="border rounded p-2 mb-2 d-flex align-items-center"
                                  >
                                    <div className="flex-grow-1">
                                      {item.product?.image && (
                                          <img
                                              src={item.product.image}
                                              alt={item.product.name || t("ecommerce_orders.unknown_product")}
                                              style={{ width: "50px", height: "50px", objectFit: "cover", marginRight: "10px" }}
                                              className="float-start"
                                          />
                                      )}
                                      <p className="mb-1">
                                        <strong>{t("ecommerce_orders.form.product")}:</strong>{" "}
                                        <Link
                                            to={`/apps-ecommerce-product-details/${item.product?._id || ""}`}
                                            className="text-primary"
                                        >
                                          {item.product?.name || t("ecommerce_orders.unknown_product")}
                                        </Link>
                                      </p>
                                      <p className="mb-1">
                                        <strong>{t("ecommerce_orders.form.brand")}:</strong> {item.product?.brand || "N/A"}
                                      </p>
                                      <p className="mb-1">
                                        <strong>{t("ecommerce_orders.form.quantity")}:</strong> {item.quantity}
                                      </p>
                                      <p className="mb-0">
                                        <strong>{t("ecommerce_orders.form.price")}:</strong> {item.price}
                                      </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-danger btn-sm"
                                        onClick={() => handleRemoveProduct(index)}
                                    >
                                      {t("ecommerce_orders.form.remove")}
                                    </button>
                                  </div>
                              ))
                          ) : (
                              <p>{t("ecommerce_orders.form.no_products")}</p>
                          )}
                          {validation.touched.items && validation.errors.items && (
                              <FormFeedback type="invalid" className="d-block">
                                {validation.errors.items}
                              </FormFeedback>
                          )}
                        </div>
                      </ModalBody>
                      <div className="modal-footer">
                        <div className="hstack gap-2 justify-content-end">
                          <button type="button" className="btn btn-light" onClick={toggle}>
                            {t("ecommerce_orders.form.close")}
                          </button>
                          <button type="submit" className="btn btn-success">
                            {t("ecommerce_orders.form.update")}
                          </button>
                        </div>
                      </div>
                    </form>
                  </Modal>
                  <ToastContainer closeButton={false} limit={1} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
  );
};

export default EcommerceOrders;